import { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";

// MUI
import { Box, Grid, Typography } from "@mui/material";

// Hooks
import { usePlaceDetails } from "@/DataPages/hooks/crm/address/usePlaceDetails";
import { useAddressValidation } from "@/DataPages/hooks/crm/address/useAddressValidation";

// Components
import SimpleTextField from "../InputComponents/SimpleTextField";
import { LabeledFormInput } from "./LabeledFormInput";
import { CountrySelector } from "./CountrySelector";
import { StateSelector } from "./StateSelector";
import { CitySelector } from "./CitySelector";
import { SmartAddressInput } from "./SmartAddressInput";
import SimpleIconButton from "../Buttons/SimpleIconButton";
import { AcceptIcon } from "./AcceptIcon";
import { RejectIcon } from "./RejectIcon";
import ZipCodeInput from "./ZipCodeInput";

// Constants
import { Z_INDEX_FOR_SELECTOR_LIST_IN_POPUP } from "@/Constants/misc";

// Helpers
import { and } from "@/Common/helpers/sonar";
import { concatAddress2 } from "./Helpers";
import { hasValue } from "@/DataPages/helpers/crm/utils";
import { isBusiness, isIndividual } from "./Helpers";
import { IGeoDetails } from "@/Common/interfaces/location";

function RecommendedAddress(
	props: Readonly<{
		googleAddress: GoogleAddressValidation | undefined;
		showRecommendedAddress: boolean;
		handleAcceptRecommendedAddress: () => void;
		handleRejectRecommendedAddress: () => void;
	}>
) {
	const { googleAddress, showRecommendedAddress, handleAcceptRecommendedAddress, handleRejectRecommendedAddress } = props;
	if (!showRecommendedAddress) {
		return null;
	}
	return (
		<Grid item xs={12} sm={12}>
			<Typography className="!font-montserrat !font-[500] !text-[16px] !text-gray-text">Recommended Address</Typography>
			<Box className="flex flex-row gap-[40px]">
				<Typography className="!text-[14.5px] !font-[400] !font-[PTSansProNarrow] !text-gray-text" sx={{ mb: 1 }}>
					{googleAddress?.recommendedAddress?.formatted ?? "This is an address, City, State, Zip Code, Country"}
				</Typography>
				<Box className="relative top-[-3px]">
					<SimpleIconButton icon={<AcceptIcon />} onClick={handleAcceptRecommendedAddress}></SimpleIconButton>
					<SimpleIconButton icon={<RejectIcon />} onClick={handleRejectRecommendedAddress}></SimpleIconButton>
				</Box>
			</Box>
		</Grid>
	);
}

type TSmartAddressFormSectionProps = {
	geoDetailValue?: IGeoDetails;
	disabled: boolean;
	variant: "individual" | "business";
	sx?: CSSProperties;
	className?: string;
	parsedAddress?: ParsedAddress;
	selectorListZIndex?: number;
	requiredFields: boolean;
	title?: string;
	loading?: boolean;
	onValidateAddressAction?: (address: Partial<ParsedAddress>) => void;
	onCallback?: () => void;
};

export function SmartAddressFormSection(props: Readonly<TSmartAddressFormSectionProps>) {
	const {
		geoDetailValue,
		variant,
		sx,
		className,
		parsedAddress,
		selectorListZIndex = Z_INDEX_FOR_SELECTOR_LIST_IN_POPUP,
		requiredFields,
		disabled,
		title,
		loading = false,
		onValidateAddressAction,
		onCallback,
	} = props;

	/* Constants */
	const commonPopperProps = { sx: { zIndex: `${selectorListZIndex}!important` } };

	/* States */
	const [address1, setAddress1] = useState<string | undefined>(parsedAddress?.address1);
	const [address2, setAddress2] = useState<string | undefined>(parsedAddress?.address2);
	const [country, setCountry] = useState<string | undefined>(parsedAddress?.countryCode);
	const [countryName, setCountryName] = useState<string | undefined>(parsedAddress?.countryName);
	const [state, setState] = useState<string | undefined>(parsedAddress?.stateCode);
	const [stateName, setStateName] = useState<string | undefined>(parsedAddress?.stateName);
	const [city, setCity] = useState<string | undefined>(parsedAddress?.city);
	const [zipCode, setZipCode] = useState<string | undefined>(parsedAddress?.zipCode);

	const [countryId, setCountryId] = useState<number | undefined>(undefined);
	const [isUsingCustomAddress, setIsUsingCustomAddress] = useState<boolean>(false);
	const [showRecommendedAddress, setShowRecommendedAddress] = useState<boolean>(false);
	const [isAddressDecided, setIsAddressDecided] = useState<boolean>(false);

	const [selectedPlaceId, setSelectedPlaceId] = useState<string | undefined>();

	/* useMemos */
	const searchAddress = useMemo(() => {
		const hasAllRegions = and(hasValue(country), hasValue(state), hasValue(stateName));
		const hasAllLocality = and(hasValue(city), hasValue(zipCode));
		const isFullyFilled = and(hasValue(address1), hasAllRegions, hasAllLocality);

		if (!isFullyFilled) {
			return "";
		}

		const address = `${address1}${concatAddress2(address2)}`.trim();

		return `${address}, ${city?.trim()}, ${stateName?.trim()} ${zipCode?.trim()}, ${country?.trim()}`;
	}, [address1, address2, country, state, stateName, city, zipCode]);

	const autocompleteBias = useMemo(() => {
		const bias = [address2, country, state, city, zipCode].filter(Boolean);
		return bias as string[];
	}, [address2, country, state, city, zipCode]);

	/* Hooks */
	const { data: googleAddress, isLoading: _isLoadingValidation } = useAddressValidation({
		address: searchAddress,
		countryCode: country,
	});

	const { details: placeDetails, isLoading: _isLoadingDetails } = usePlaceDetails({ placeId: selectedPlaceId });

	/* useCallBacks */
	const handleAddressValidationAction = useCallback(() => {
		if (!googleAddress) {
			return;
		}

		if (googleAddress.isValid && googleAddress?.recommendedAddress) {
			const location = googleAddress.geocode.location;
			const { parsed: validNewAddress } = googleAddress.recommendedAddress;
			onValidateAddressAction?.({ ...validNewAddress, location });
			return;
		}

		const newAddress: Partial<ParsedAddress> = {
			address1: address1 ?? "",
			address2: address2 ?? "",
			countryCode: country ?? "",
			countryName: countryName,
			stateCode: state ?? "",
			city: city ?? "",
			zipCode: zipCode ?? "",
			location: googleAddress.geocode.location,
		};

		onValidateAddressAction?.(newAddress);
	}, [googleAddress]);

	const setAddressDetails = useCallback(
		(details?: PlaceDetails | GoogleAddressValidation["recommendedAddress"]) => {
			setAddress1(details?.parsed.address1);
			setAddress2(details?.parsed.address2);
			setCountry(details?.parsed.countryCode);
			setState(details?.parsed.stateCode);
			setStateName(details?.parsed.stateName);
			setCity(details?.parsed.city);
			setZipCode(details?.parsed.zipCode);
		},
		[setAddress1, setAddress2, setCountry, setState, setStateName, setCity, setZipCode]
	);

	/* Handlers */
	const handleRejectRecommendedAddress = () => {
		setShowRecommendedAddress((prev) => !prev);
		setIsUsingCustomAddress(true);
		setIsAddressDecided(true);
		handleAddressValidationAction();
	};

	const handleAcceptRecommendedAddress = () => {
		if (googleAddress?.recommendedAddress) {
			setShowRecommendedAddress(false);
			setIsUsingCustomAddress(false);
			setAddressDetails(googleAddress.recommendedAddress);
			setIsAddressDecided(true);
			handleAddressValidationAction();
		}
	};

	/* Utils */
	function cleanState() {
		setState(undefined);
		setStateName(undefined);
		cleanCity();
	}

	function cleanCity() {
		setCity(undefined);
		cleanZipCode();
	}

	function cleanZipCode() {
		setZipCode(undefined);
		handleOnCallback();
		setSelectedPlaceId(undefined);
	}

	function handleOnCallback() {
		if (searchAddress && onCallback) {
			onCallback();
		}
	}

	function resetAllFields() {
		setAddress1(undefined);
		setAddress2(undefined);
		setCountry(undefined);
		setState(undefined);
		setStateName(undefined);
		setCity(undefined);
		setZipCode(undefined);
		setCountryId(undefined);
		setSelectedPlaceId(undefined);
		setIsUsingCustomAddress(false);
		setShowRecommendedAddress(false);
		setIsAddressDecided(false);
		handleOnCallback();
	}

	/* useEffects */
	useEffect(() => {
		if (placeDetails) {
			setAddressDetails(placeDetails);
		}
	}, [placeDetails]);

	useEffect(() => {
		if (!googleAddress) {
			return;
		}

		const hasReplacedComponents = googleAddress.verdict.hasReplacedComponents ?? false;
		const hasInferredComponents = googleAddress.verdict.hasInferredComponents ?? false;
		const hasUsefulRecommendation = hasReplacedComponents || hasInferredComponents;
		const isCustomerIndividual = isIndividual(variant);
		const shouldShowRecommendedAddress = !isAddressDecided && isCustomerIndividual && hasUsefulRecommendation;

		setShowRecommendedAddress(shouldShowRecommendedAddress);

		if (!isUsingCustomAddress) {
			setAddressDetails(googleAddress.recommendedAddress);
		}

		if (!isCustomerIndividual || !shouldShowRecommendedAddress) {
			handleAddressValidationAction();
		}
	}, [googleAddress, isUsingCustomAddress]);

	useEffect(() => {
		if (isBusiness(variant)) {
			resetAllFields();
		}
	}, [variant]);

	useEffect(() => {
		if (geoDetailValue) {
			setAddress1(geoDetailValue.address);
			setAddress2(geoDetailValue.address2);
			setCity(geoDetailValue.city);
			setState(geoDetailValue.state);
			setCountry(geoDetailValue.countryCode);
			setZipCode(geoDetailValue.zipCode);
		}
	}, [geoDetailValue]);

	return (
		<Box sx={sx} className={className}>
			{title && <Typography className="!font-montserrat !font-[500] !text-[16px] !text-gray-text !mb-3">{title}</Typography>}
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6} sx={{ position: "relative" }}>
					<SmartAddressInput
						addable={variant === "individual"}
						onSelectedAddress={(selected) => {
							setIsAddressDecided(false);
							const isAddedOption = Object.keys(selected ?? {}).includes("addedOption");
							setIsUsingCustomAddress(isAddedOption);
							setAddress1(selected?.searchableText ?? "");
							setSelectedPlaceId(!isAddedOption ? selected?.placeId : "");
							if (!selected) {
								handleOnCallback();
							}
						}}
						inferredAddress={address1}
						autocompleteBias={autocompleteBias}
						selectorProps={{ popperProps: commonPopperProps }}
						required={requiredFields}
						disabled={disabled}
						loading={loading}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<LabeledFormInput
						label="Address 2"
						formInput={
							<SimpleTextField
								sx={{}}
								editable
								outlined
								text={address2}
								placeholder={"Address 2"}
								onActionUpdate={(valueAddress2) => {
									setAddress2(valueAddress2);
								}}
								disabled={disabled || loading}
								inputProps={{}}
							/>
						}
					/>
				</Grid>
				<Grid item xs={12} sm={3} className="!-mt-1">
					<CountrySelector
						onCountrySelectedAction={(selectedCountry) => {
							setCountryId(selectedCountry?.id);
							setCountry(selectedCountry?.code);
							setCountryName(selectedCountry?.name);
							setIsAddressDecided(false);
							cleanState();
						}}
						onUpdateCountryId={(id) => setCountryId(id)}
						inferredCountry={{ code: country ?? "", name: placeDetails?.parsed.countryName ?? "" }}
						selectorProps={{ popperProps: commonPopperProps }}
						required={requiredFields}
						disabled={disabled}
						loading={loading}
					/>
				</Grid>
				<Grid item xs={12} sm={3} className="!-mt-1">
					<StateSelector
						countryId={countryId}
						onStateSelectedAction={(selectedState) => {
							setState(selectedState?.code);
							setStateName(selectedState?.name);
							setIsAddressDecided(false);
							cleanCity();
						}}
						inferredState={{
							countryCode: country ?? "",
							countryName: placeDetails?.parsed.countryName ?? "",
							code: state ?? "",
							name: stateName ?? placeDetails?.parsed.stateName ?? "",
						}}
						selectorProps={{ popperProps: commonPopperProps }}
						required={requiredFields}
						disabled={disabled}
						loading={loading}
					/>
				</Grid>
				<Grid item xs={12} sm={3} className="!-mt-1">
					<CitySelector
						countryCode={country}
						stateCode={state}
						onCitySelectedAction={(selectedCity) => {
							setCity(selectedCity?.name);
							setIsAddressDecided(false);
							cleanZipCode();
						}}
						inferredCity={{
							countryCode: country ?? "",
							countryName: placeDetails?.parsed.countryName ?? "",
							stateCode: state ?? "",
							stateName: stateName ?? placeDetails?.parsed.stateName ?? "",
							name: city ?? "",
						}}
						selectorProps={{ popperProps: commonPopperProps }}
						required={requiredFields}
						disabled={disabled}
						loading={loading}
					/>
				</Grid>
				<Grid item xs={12} sm={3} className="!-mt-1">
					<ZipCodeInput
						zipCode={zipCode}
						setZipCode={(zipCodeValue) => {
							setZipCode(zipCodeValue);
							if (!zipCodeValue) {
								handleOnCallback();
							}
						}}
						required={requiredFields}
						disabled={disabled}
						loading={loading}
					/>
				</Grid>
				<RecommendedAddress
					googleAddress={googleAddress}
					showRecommendedAddress={showRecommendedAddress}
					handleAcceptRecommendedAddress={handleAcceptRecommendedAddress}
					handleRejectRecommendedAddress={handleRejectRecommendedAddress}
				/>
			</Grid>
		</Box>
	);
}

export default SmartAddressFormSection;
